import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Sidebar from '../home/components/Sidebar';
import { getCurrentUser } from '../../services/auth';
import { deductCredits } from '../../services/credits';
import { createHistory } from '../../services/history';
import { getStyleTransferModelConfig, generateStyleTransfer, type StyleTransferModelConfig } from '../../services/styleTransfer';

const STYLE_TRANSFER_CREDITS_PER_USE = 5;

const STYLE_PROMPTS: Record<string, string> = {
  绘画: '将画面改为艺术绘画风格，油画质感，印象派笔触，色彩饱满，艺术感强',
  水墨: '将画面改为中国传统水墨画风格，墨色晕染，留白意境，水墨韵味',
  彩绘: '将画面改为数字彩绘风格，明亮色彩，立体感强，插画感，视觉冲击力强',
};

export default function StyleTransferPage() {
  const [activeMenu, setActiveMenu] = useState('智能风格转换');
  const [uploadedImage, setUploadedImage] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('绘画');
  const [generatedImage, setGeneratedImage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<Awaited<ReturnType<typeof getCurrentUser>>>(null);
  const [styleTransferConfig, setStyleTransferConfig] = useState<StyleTransferModelConfig | null>(null);

  const styles = [
    { id: '绘画', label: '绘画', description: '艺术绘画风格，适合营造艺术氛围和审美效果' },
    { id: '水墨', label: '水墨', description: '墨色晕染效果，呈现传统意境和韵味的艺术风格' },
    { id: '彩绘', label: '彩绘', description: '数字用明亮色彩和立体感的绘画风格，呈现艺术美感和视觉冲击力' },
  ];

  useEffect(() => {
    const load = async () => {
      const [u, config] = await Promise.all([getCurrentUser(), getStyleTransferModelConfig()]);
      setUser(u);
      setStyleTransferConfig(config ?? null);
    };
    load();
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setError(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setUploadedImage('');
  };

  const handleGenerate = async () => {
    if (!uploadedImage) return;
    if (!user?.id) {
      setError('请先登录');
      return;
    }
    if (!styleTransferConfig?.model_name) {
      setError('请先在后台配置并启用图像模型，用于风格转换');
      return;
    }
    if (styleTransferConfig.provider !== 'alibaba' && !styleTransferConfig.api_key) {
      setError('当前图像模型缺少可用密钥。请启用阿里图像模型，或重新配置火山引擎/钱多多密钥');
      return;
    }
    setError(null);
    setIsGenerating(true);
    try {
      const deduct = await deductCredits(user.id, STYLE_TRANSFER_CREDITS_PER_USE, '智能风格转换', undefined);
      if (!deduct.success) {
        setError(deduct.error ?? '积分不足');
        return;
      }
      const prompt = STYLE_PROMPTS[selectedStyle] || STYLE_PROMPTS['绘画'];
      const imageUrl = await generateStyleTransfer(styleTransferConfig, uploadedImage, prompt, 'adaptive');
      setGeneratedImage(imageUrl);
      await createHistory(user.id, {
        type: 'tool',
        category: 'style-transfer',
        prompt,
        input_images: uploadedImage ? [uploadedImage] : undefined,
        output_url: imageUrl,
        status: 'succeeded',
        credits_used: STYLE_TRANSFER_CREDITS_PER_USE,
        settings: { style: selectedStyle },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : '风格转换失败，请重试');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `style-transfer-${Date.now()}.png`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex min-w-0">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        
        <main className="flex-1 main-content-padding overflow-y-auto overflow-x-hidden min-w-0">
          <div className="max-w-7xl mx-auto w-full min-w-0">
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">风格转换</h1>
              <p className="text-gray-400 text-sm">上传参考图片选择风格，素描、彩绘等风格随心选，瞬间生成全新画面。</p>
              {error && (
                <div className="mt-3 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {error}
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 min-w-0">
              {/* Left Panel - Input Controls */}
              <div className="space-y-6 min-w-0">
                {/* Image Upload */}
                <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-image-add-line text-blue-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                    <h2 className="text-lg font-semibold">参考图片</h2>
                  </div>
                  
                  {uploadedImage ? (
                    <div className="relative group">
                      <div className="w-full h-64">
                        <img 
                          src={uploadedImage} 
                          alt="Uploaded" 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      <button
                        onClick={handleRemoveImage}
                        className="absolute top-3 right-3 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      >
                        <i className="ri-close-line text-white text-lg"></i>
                      </button>
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <label className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm cursor-pointer whitespace-nowrap backdrop-blur-sm">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                          重新上传
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-700 rounded-lg p-16 text-center hover:border-blue-500/50 transition-colors cursor-pointer relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-blue-600/10 rounded-full flex items-center justify-center">
                          <i className="ri-upload-cloud-line text-blue-400 text-3xl"></i>
                        </div>
                        <p className="text-gray-300 font-medium">拖拽图片到此处，或点击上传</p>
                        <p className="text-gray-500 text-xs">支持JPG/PNG/WEBP格式，单张图片不超过10MB</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Style Selection */}
                <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-palette-line text-purple-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                    <h2 className="text-lg font-semibold">风格选择</h2>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    {styles.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => setSelectedStyle(style.id)}
                        className={`px-4 py-8 rounded-lg text-center transition-all cursor-pointer border ${
                          selectedStyle === style.id
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-[#0a0a0a] text-gray-300 hover:bg-[#252525] border-gray-700'
                        }`}
                      >
                        <div className="text-base font-semibold mb-2">{style.label}</div>
                        <div className="text-xs text-gray-400 leading-relaxed">{style.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={!uploadedImage || isGenerating}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-700 disabled:to-gray-700 text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      生成中...
                    </>
                  ) : (
                    <>
                      <i className="ri-sparkling-2-fill"></i>
                      立即生成
                      <span className="bg-yellow-500 text-black text-xs px-2 py-1 rounded-full">{STYLE_TRANSFER_CREDITS_PER_USE} 积分</span>
                    </>
                  )}
                </button>
              </div>

              {/* Right Panel - Preview */}
              <div className="space-y-6 min-w-0">
                <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800 sticky top-8">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-image-line text-green-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                    <h2 className="text-lg font-semibold">生成效果</h2>
                  </div>
                  
                  <div className="bg-[#0a0a0a] rounded-lg overflow-hidden border border-gray-800 aspect-square flex items-center justify-center">
                    {isGenerating ? (
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-gray-400 text-sm">AI正在生成中，请稍候...</p>
                      </div>
                    ) : generatedImage ? (
                      <div className="relative w-full h-full group">
                        <img 
                          src={generatedImage} 
                          alt="Generated" 
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                          <button onClick={handleDownload} className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer" title="下载">
                            <i className="ri-download-line text-white text-xl"></i>
                          </button>
                          <a href={generatedImage} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer" title="新窗口打开">
                            <i className="ri-share-line text-white text-xl"></i>
                          </a>
                          <button onClick={() => setGeneratedImage('')} className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer" title="清除">
                            <i className="ri-delete-bin-line text-white text-xl"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3 text-gray-500">
                        <div className="w-24 h-24 bg-gray-800/50 rounded-full flex items-center justify-center">
                          <i className="ri-image-line text-5xl"></i>
                        </div>
                        <p className="text-sm">生成的图片将在此展示</p>
                      </div>
                    )}
                  </div>

                  {/* Tips */}
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-lightbulb-line text-yellow-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                      <h3 className="text-sm font-semibold">生成技巧</h3>
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-400">
                      <div className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-blue-400 mt-0.5 flex-shrink-0"></i>
                        <p>上传参考图片后选择合适的风格，系统会自动进行风格化处理</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-blue-400 mt-0.5 flex-shrink-0"></i>
                        <p>不同风格适合不同场景，可以多次尝试找到最佳效果</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-blue-400 mt-0.5 flex-shrink-0"></i>
                        <p>选择合适的画面比例可以让生成的图片更符合使用需求</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
